import React from "react";
import Toaster from "../Toaster";
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from "../CclDataTable";
import ApiAdminUsers from "../../code/api/ApiAdminUsers";
import { AdminUserModel } from "../../code/models/AdminUserModel";
import ApiCompany from "../../code/api/ApiCompany";
import Company from "../../code/models/Company";
import AdminRole from "../../code/models/AdminRole";
import ApiAdminRole from "../../code/api/ApiAdminRole";
import "../../css/adminusermanagement.css";
import ButtonConfirm, { ButtonConfirmSize } from "../ButtonConfirm";

export default function AdminUserManagement() {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [adminUsers, setAdminUsers] = React.useState<AdminUserModel[]>([]);
    const [selectedAdminUser, setSelectedAdminUser] = React.useState<AdminUserModel | null>(null);
    const [editAdminUser, setEditAdminUser] = React.useState<AdminUserModel | null>(null);

    const [companies, setCompanies] = React.useState<Company[]>([]);
    const [adminRoles, setAdminRoles] = React.useState<AdminRole[]>([]);

    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);

    React.useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        await Promise.all([
            ApiAdminUsers.getAdminUsers().then((users) => {
                setAdminUsers(users);
            }),
            ApiCompany.getCompanies().then((companies) => {
                setCompanies(companies);
            }),
            ApiAdminRole.getAdminRoles().then((roles) => {
                setAdminRoles(roles);
            })
        ]);
    }

    function rowSelected(rowData: AdminUserModel): void {
        setSelectedAdminUser(rowData);
        setEditAdminUser(structuredClone(rowData));
        setAnyChanges(false);
    }

    function clearRowSelected(): void {
        setSelectedAdminUser(null);
        setEditAdminUser(null);
        setAnyChanges(false);
    }

    function checkHasChanges() {
        if (editAdminUser == null || selectedAdminUser == null) {
            setAnyChanges(false);
            return;
        }

        var isAnyChanges =
            selectedAdminUser.firstName !== editAdminUser.firstName ||
            selectedAdminUser.lastName !== editAdminUser.lastName ||
            selectedAdminUser.logonId !== editAdminUser.logonId ||
            selectedAdminUser.password !== editAdminUser.password;

        if (!isAnyChanges) {
            // check company and role
            isAnyChanges = selectedAdminUser.adminRoles.length !== editAdminUser.adminRoles.length ||
                selectedAdminUser.companies.length !== editAdminUser.companies.length;
            if (!isAnyChanges) {
                let arChanges = selectedAdminUser.adminRoles.some((original) => {
                    return editAdminUser.adminRoles.findIndex((updated) => {
                        return updated.adminRoleId === original.adminRoleId;
                    }) <= -1;
                });

                let compChanges = selectedAdminUser.companies.some((original) => {
                    return editAdminUser.companies.findIndex((updated) => {
                        return updated.companyId === original.companyId;
                    }) <= -1;
                });

                isAnyChanges = arChanges || compChanges;
            }
        }

        setAnyChanges(isAnyChanges);
    }

    function formatName(val, rowData: AdminUserModel) {
        return `${rowData.firstName} ${rowData.lastName}`;
    }

    const admUserColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "firstName",
            title: "Admin",
            visible: true,
            formatData: formatName,
            type: CclDataTableColumnType.Data
        }
    ];

    function saveAdminUserClick(): void {
        if (anyChanges && !!editAdminUser && !!selectedAdminUser) {
            ApiAdminUsers.updateAdminUser(editAdminUser).then((savedUser) => {
                let isNew = selectedAdminUser.adminUserId <= 0;

                setToastMessage(`The admin user has been ${isNew ? "Added" : "Saved"}.`);

                if (isNew) {
                    setAdminUsers([...adminUsers, savedUser]);
                    rowSelected(savedUser);
                } else {
                    selectedAdminUser.adminRoles = editAdminUser.adminRoles;
                    selectedAdminUser.companies = editAdminUser.companies;
                    selectedAdminUser.firstName = editAdminUser.firstName;
                    selectedAdminUser.lastName = editAdminUser.lastName;
                    selectedAdminUser.logonId = editAdminUser.logonId;
                    selectedAdminUser.password = "";

                    editAdminUser.password = "";
                    
                    setAnyChanges(false);
                }
            });
        }
    }

    function addAdminUserClick(): void {
        let newUser = new AdminUserModel();
        newUser.adminUserId = 0;
        rowSelected(newUser);
    }

    function deleteAdminUserClick(confirm: boolean): void {
        if (!selectedAdminUser || !confirm) return;

        ApiAdminUsers.deleteAdminUser(selectedAdminUser.adminUserId).then((result) => {
            if (result) {
                setToastMessage("The admin user has been deleted.");
                clearRowSelected();
                loadData();
            }
        });
    }

    function companyCheckChange(comp: Company, checked: boolean) {
        if (!editAdminUser) return;

        let compIdx = editAdminUser.companies.findIndex((c) => c.companyId === comp.companyId);

        if (checked && compIdx < 0) {
            editAdminUser.companies.push(comp);
        } else if (!checked && compIdx > -1) {
            editAdminUser.companies.splice(compIdx, 1);
        }
        checkHasChanges();
    }

    function buildCompanies() {
        return companies.map((comp) => {
            let hasCompany = !!selectedAdminUser &&
                selectedAdminUser.companies.findIndex((c) => c.companyId === comp.companyId) > -1;
            return (
                <li className="list-group-item" key={`aumgt-comp-${comp.companyId}${hasCompany}`}>
                    <input className="form-check-input me-1" type="checkbox" id={`${comp.code}checkbox`}
                        defaultChecked={hasCompany} disabled={!editAdminUser}
                        onChange={(e) => { companyCheckChange(comp, e.target.checked); }} />
                    <label className="form-check-label" htmlFor={`${comp.code}checkbox`}>{comp.name}</label>
                </li>
            )
        });
    }

    function roleCheckChange(role: AdminRole, checked: boolean) {
        if (!editAdminUser) return;

        let roleIdx = editAdminUser.adminRoles.findIndex((r) => r.adminRoleId === role.adminRoleId);

        if (checked && roleIdx < 0) {
            editAdminUser.adminRoles.push(role);
        } else if (!checked && roleIdx > -1) {
            editAdminUser.adminRoles.splice(roleIdx, 1);
        }
        checkHasChanges();
    }

    function buildRoles() {
        return adminRoles.map((role) => {
            let hasRole = !!selectedAdminUser &&
                selectedAdminUser.adminRoles.findIndex((r) => r.adminRoleId === role.adminRoleId) > -1;
            return (
                <li className="list-group-item" key={`aumgt-comp-${role.adminRoleId}${hasRole}`}>
                    <input className="form-check-input me-1" type="checkbox" id={`${role.name}checkbox`}
                        defaultChecked={hasRole} disabled={!editAdminUser}
                        onChange={(e) => { roleCheckChange(role, e.target.checked); }} />
                    <label className="form-check-label" htmlFor={`${role.name}checkbox`}>{role.name}</label>
                    <div className="adminusermgt-desc">{role.description}</div>
                </li>
            )
        });
    }

    return (
        <React.Fragment>
            <div className="container-fluid h-100">
                <div className="row h-100">
                    <div className="col adminusermgt-namediv">
                        <CclDataTable key={`aumgtdata` + (selectedAdminUser?.adminUserId || 0)}
                            name={"apiUsersTable"}
                            tableData={adminUsers || []}
                            columnDefinitions={admUserColumnDefs}
                            onRowSelect={rowSelected}
                            rowSelect={true}
                            onButtonClick={null}
                            defaultSelectedRow={selectedAdminUser} />
                    </div>
                    <div className="col adminusermgt-editordiv" key={`aumgtedt` + (selectedAdminUser?.adminUserId || 0)}>
                        <div className="row">
                            <label htmlFor="templateSubject" className="form-label">User Id</label>
                            <input className="form-control" disabled={!editAdminUser}
                                id="templateSubject" defaultValue={selectedAdminUser?.logonId || ""} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editAdminUser) {
                                        editAdminUser.logonId = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="row">
                            <label htmlFor="templateSubject" className="form-label">First Name</label>
                            <input className="form-control" disabled={!editAdminUser}
                                id="templateSubject" defaultValue={selectedAdminUser?.firstName || ""} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editAdminUser) {
                                        editAdminUser.firstName = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="row">
                            <label htmlFor="templateSubject" className="form-label">Last Name</label>
                            <input className="form-control" disabled={!editAdminUser}
                                id="templateSubject" defaultValue={selectedAdminUser?.lastName || ""} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editAdminUser) {
                                        editAdminUser.lastName = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="row">
                            <label htmlFor="templateSubject" className="form-label">Password</label>
                            <input className="form-control" disabled={!editAdminUser}
                                id="templateSubject" defaultValue={selectedAdminUser?.password || ""} autoComplete={"off"}
                                onChange={(e) => {
                                    if (!!editAdminUser) {
                                        editAdminUser.password = e.target.value;
                                        checkHasChanges();
                                    }
                                }}></input>
                        </div>
                        <div className="row">
                            <button className={"btn btn-sm btn-primary adminusermgt-savebtn"}
                                disabled={!editAdminUser || !anyChanges} onClick={saveAdminUserClick}>Save User</button>
                        </div>
                        <div className="row">
                            <button className={"btn btn-sm btn-primary adminusermgt-addbtn"}
                                onClick={addAdminUserClick}>Add User</button>
                        </div>
                        <div className="row">
                            <ButtonConfirm enabled={!!selectedAdminUser} buttonText={"Delete User"}
                                confirmTitle={"Delete User"} confirmMessage={`Delete current user "${selectedAdminUser?.firstName} ${selectedAdminUser?.lastName}"?`}
                                buttonSize={ButtonConfirmSize.Small} cssClass={"adminusermgt-deletebtn"}
                                onConfirm={deleteAdminUserClick} />
                        </div>
                    </div>
                    <div className="col h-100 overflow-y-auto adminusermgt-assigndiv">
                        <div className="row">
                            <label className="form-label adminusermgt-title">Companies</label>
                            <ul className="list-group">
                                {buildCompanies()}
                            </ul>
                        </div>
                        <div className="row">
                            <label className="form-label adminusermgt-title pad-top-10">Roles</label>
                            <ul className="list-group">
                                {buildRoles()}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <Toaster title="Admin Users" subtitle="User" message={toastMessage} key={"adminusermgttoast"} />
        </React.Fragment>
    )
}
